import {
  Button,
  ConfirmAction,
  DataTable,
  FileThumb,
  ImageViewer,
  type ImageViewerSlide,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
} from '@skylabs-digital/backoffice-kit';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { Download, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type FilesPage, type FilesQuery, useFileActions } from '../../api/files.api';
import type { FistoFile } from '../../schemas/file.schema';
import { isImage, thumbUrl, toSlides, variantOf } from '../files/file-slides';
import { formatBytes } from './AppList';

/** Una fila abre el visor sólo si hay algo que mostrar: imagen ya subida con URL. */
const canPreview = (f: FistoFile): boolean =>
  isImage(f) && f.status === 'UPLOADED' && f.url !== null;

function VariantsChip({ f }: { f: FistoFile }) {
  if (f.processing === 'QUEUED') return <StatusBadge tone="info">processing…</StatusBadge>;
  if (f.processing === 'FAILED') return <StatusBadge tone="danger">variants failed</StatusBadge>;
  if (f.variants.length === 0) return null;
  const n = f.variants.length;
  return (
    <StatusBadge tone="neutral" dot={false}>
      {n} {n === 1 ? 'variant' : 'variants'}
    </StatusBadge>
  );
}

const LIMIT = 25;
const ANY_STATUS = '__any__';
type StatusFilter = NonNullable<FilesQuery['status']> | typeof ANY_STATUS;

export function AppFilesTable({ appId }: { appId: string }) {
  const files = useFileActions();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(1);
  const [prefix, setPrefix] = useState('');
  const [status, setStatus] = useState<StatusFilter>(ANY_STATUS);
  const [result, setResult] = useState<FilesPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [viewer, setViewer] = useState<FistoFile | null>(null);
  const slides = useMemo<ImageViewerSlide[]>(() => (viewer ? toSlides(viewer) : []), [viewer]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setResult(
        await files.list(appId, {
          page,
          limit: LIMIT,
          prefix: prefix || undefined,
          status: status === ANY_STATUS ? undefined : status,
        }),
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load files'));
    } finally {
      setLoading(false);
    }
  }, [appId, page, prefix, status, files.list]);

  useEffect(() => {
    void load();
  }, [load]);

  const download = useCallback(
    async (f: FistoFile, variant?: string) => {
      try {
        const { url } = await files.downloadUrl(appId, f.id, variant);
        window.open(url, '_blank', 'noopener');
      } catch {
        showSnackbar({ message: 'Could not get a download URL', variant: 'error' });
      }
    },
    [appId, files.downloadUrl, showSnackbar],
  );

  const remove = useCallback(
    async (f: FistoFile) => {
      try {
        await files.remove(appId, f.id);
        showSnackbar({ message: `Deleted ${f.path}`, variant: 'success' });
        await load();
      } catch {
        showSnackbar({ message: 'Failed to delete file', variant: 'error' });
      }
    },
    [appId, files.remove, showSnackbar, load],
  );

  const columns = useMemo<ColumnDef<FistoFile>[]>(
    () => [
      {
        accessorKey: 'path',
        header: 'Path',
        cell: ({ row }) => {
          const f = row.original;
          const inner = (
            <>
              <FileThumb src={thumbUrl(f)} contentType={f.contentType} size={36} />
              <code className="min-w-0 truncate font-mono text-xs">{f.path}</code>
              <VariantsChip f={f} />
            </>
          );
          return canPreview(f) ? (
            <button
              type="button"
              onClick={() => setViewer(f)}
              aria-label={`Preview ${f.path}`}
              className="flex max-w-full items-center gap-2 rounded-md text-left hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {inner}
            </button>
          ) : (
            <span className="flex max-w-full items-center gap-2">{inner}</span>
          );
        },
      },
      {
        accessorKey: 'contentType',
        header: 'Type',
        cell: ({ row }) => <span className="text-sm">{row.original.contentType}</span>,
      },
      {
        accessorKey: 'size',
        header: 'Size',
        cell: ({ row }) => <span className="text-sm">{formatBytes(row.original.size)}</span>,
      },
      {
        accessorKey: 'visibility',
        header: 'Visibility',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.visibility === 'PUBLIC' ? 'warning' : 'info'}>
            {row.original.visibility}
          </StatusBadge>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <span className="flex items-center gap-1">
            <StatusBadge tone={row.original.status === 'UPLOADED' ? 'success' : 'neutral'}>
              {row.original.status}
            </StatusBadge>
            {row.original.processing !== 'NONE' ? (
              <StatusBadge
                tone={
                  row.original.processing === 'FAILED'
                    ? 'danger'
                    : row.original.processing === 'DONE'
                      ? 'success'
                      : 'info'
                }
              >
                {row.original.processing.toLowerCase()}
              </StatusBadge>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-sm">{new Date(row.original.createdAt).toLocaleString()}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <span className="flex items-center gap-1">
            {row.original.status === 'UPLOADED' ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => download(row.original)}
                aria-label="Download"
              >
                <Download />
              </Button>
            ) : null}
            <ConfirmAction
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  aria-label="Delete"
                >
                  <Trash2 />
                </Button>
              }
              title="Delete this file?"
              description="The object and its variants are removed from storage. The record stays as DELETED for audit."
              confirmLabel="Delete file"
              destructive
              onConfirm={() => remove(row.original)}
            />
          </span>
        ),
      },
    ],
    [download, remove],
  );

  return (
    <div className="space-y-3">
      <ImageViewer
        open={viewer !== null}
        onOpenChange={(open) => {
          if (!open) setViewer(null);
        }}
        slides={slides}
        onDownload={(slide) => {
          if (viewer) void download(viewer, variantOf(viewer, slide));
        }}
      />
      <div className="flex items-center gap-2">
        <Input
          placeholder="Filter by prefix (photos/2026/)"
          value={prefix}
          onChange={(e) => {
            setPage(1);
            setPrefix(e.target.value);
          }}
          className="max-w-sm"
        />
        <Select
          value={status}
          onValueChange={(v) => {
            setPage(1);
            setStatus(v as StatusFilter);
          }}
        >
          <SelectTrigger className="w-40" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_STATUS}>Any status</SelectItem>
            <SelectItem value="UPLOADED">Uploaded</SelectItem>
            <SelectItem value="PENDING">Pending upload</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {result ? `${result.meta.total} files` : ''}
        </span>
      </div>
      <DataTable columns={columns} data={result?.data ?? []} isLoading={loading} error={error} />
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!result?.meta.hasPrev}
          onClick={() => setPage((p) => p - 1)}
        >
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">
          Page {page}
          {result ? ` / ${Math.max(1, result.meta.totalPages)}` : ''}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={!result?.meta.hasNext}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
